import { 
    getAuth, 
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    User,
    AuthError,
    AuthErrorCodes
} from 'firebase/auth';
import { doc, setDoc, getDoc, getFirestore } from 'firebase/firestore';

const auth = getAuth();
const db = getFirestore();
const USERS_COLLECTION = 'cruiseclick_users';

export interface UserData {
    email: string;
    firstName: string;
    lastName: string;
    role: 'admin' | 'user';
    createdAt: Date;
}

export class AuthService {
    static async signUp(email: string, password: string, firstName: string, lastName: string): Promise<User> {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            
            // Create user profile in Firestore
            await setDoc(doc(db, USERS_COLLECTION, userCredential.user.uid), {
                email,
                firstName,
                lastName,
                role: 'user', // Default role
                createdAt: new Date()
            });

            return userCredential.user;
        } catch (error) {
            // Ensure we're throwing the original Firebase error
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('An unknown error occurred during signup');
        }
    }

    static async signIn(email: string, password: string): Promise<User> {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            return userCredential.user;
        } catch (error) {
            // Log the error for debugging
            console.log('Firebase Auth Error:', {
                error,
                isAuthError: error instanceof Error,
                errorCode: (error as AuthError)?.code,
                errorMessage: (error as Error)?.message
            });
            // Ensure we're throwing the original Firebase error
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('An unknown error occurred during sign in');
        }
    }

    static async signOut(): Promise<void> {
        try {
            await signOut(auth);
        } catch (error: any) {
            throw new Error(error.message);
        }
    }

    static onAuthStateChanged(callback: (user: User | null) => void): () => void {
        return onAuthStateChanged(auth, callback);
    }

    static getCurrentUser(): User | null {
        return auth.currentUser;
    }

    static async isAdmin(userId: string): Promise<boolean> {
        try {
            const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId));
            if (userDoc.exists()) {
                const userData = userDoc.data() as UserData;
                return userData.role === 'admin';
            }
            return false;
        } catch (error) {
            console.error('Error checking admin status:', error);
            return false;
        }
    }

    static async setAdminRole(userId: string): Promise<void> {
        try {
            await setDoc(doc(db, USERS_COLLECTION, userId), {
                role: 'admin'
            }, { merge: true });
        } catch (error) {
            throw new Error('Failed to set admin role');
        }
    }

    static async resetPassword(email: string): Promise<void> {
        try {
            const auth = getAuth();
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            console.error('Password reset error:', error);
            throw error;
        }
    }
} 